/**
 * Column Factory for Service Detail Tables
 *
 * Provides reusable column definitions for DataTable.
 * Reduces duplication across template-specific config files.
 *
 * Usage:
 * ```tsx
 * import {
 *   createBaseColumns,
 *   createPriceColumns,
 *   createDateColumns,
 *   createConfigColumn,
 *   SERVICE_STATUS_OPTIONS
 * } from '@/modules/Service/utils/service-detail/column-factory'
 *
 * const columns = [
 *   ...createBaseColumns(t),
 *   ...createConfigColumns(rows, t),
 *   ...createPriceColumns(t),
 *   ...createDateColumns(t),
 * ]
 * ```
 */
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import type { TFunction } from 'i18next'
import { formatNumberUsage } from '@/utils/function'
import type { ServiceDetailRow } from './types'
import { getProcessorModel, getCpuCapacity } from './utils'

// Re-export from split modules
export {
    SERVICE_STATUS_OPTIONS,
    createBaseColumns,
    createPriceColumns,
    createDateColumns,
    createContractColumn,
} from './base-columns'

export type { ConfigColumnOptions } from './config-columns'
export { createConfigColumn, createConfigColumns } from './config-columns'

export { CONFIG_PRESETS } from './config-presets'
export type { ConfigPresetKey } from './config-presets'

// ============================================================================
// FULL COLUMN BUILDERS
// ============================================================================

import {
    createBaseColumns,
    createPriceColumns,
    createDateColumns,
    createContractColumn,
} from './base-columns'
import { createConfigColumns, ConfigColumnOptions } from './config-columns'
import { CONFIG_PRESETS, ConfigPresetKey } from './config-presets'

export interface BuildColumnsOptions {
    t: TFunction
    rows: ServiceDetailRow[]
    configPresets?: ConfigPresetKey[]
    customConfigs?: ConfigColumnOptions[]
    includeProcessor?: boolean
}

/**
 * Build complete columns array for a template
 */
export const buildServiceDetailColumns = (
    options: BuildColumnsOptions
): DataTableColumn<ServiceDetailRow>[] => {
    const {
        t,
        rows,
        configPresets = [],
        customConfigs = [],
        includeProcessor = false,
    } = options

    const columns: DataTableColumn<ServiceDetailRow>[] = [
        ...createBaseColumns(t),
    ]

    // Add processor column for Server templates
    if (includeProcessor) {
        columns.push({
            key: 'processor',
            title: t('server_processor', { defaultValue: 'Server/Processor' }),
            width: 200,
            render: (_, row) => {
                if (row._rowType === 'total') return '-'
                const model = getProcessorModel(row.configoptions)
                return model || '-'
            },
        })
        // Add CPU capacity for Server templates
        columns.push({
            key: 'cpu',
            title: 'CPU',
            width: 80,
            cellClassName: 'text-right',
            render: (_, row) => {
                const value = getCpuCapacity(row.configoptions)
                if (row._rowType === 'total') {
                    return value ? (
                        <span className="font-medium text-primary">
                            {formatNumberUsage(value)}
                        </span>
                    ) : (
                        '-'
                    )
                }
                return value ? formatNumberUsage(value) : '-'
            },
        })
    }

    // Add preset configs
    const presetConfigs = configPresets.map((preset) => CONFIG_PRESETS[preset])
    columns.push(...createConfigColumns(presetConfigs, rows))

    // Add custom configs
    if (customConfigs.length > 0) {
        columns.push(...createConfigColumns(customConfigs, rows))
    }

    // Add price, date, and contract columns
    columns.push(
        ...createPriceColumns(t),
        ...createDateColumns(t),
        createContractColumn(t)
    )

    return columns
}
